import api from "./axios";
import { getErrorMsgAndCode } from "./FrontendHelpers";

const IS_TEST_ENV = process.env.REACT_APP_ENV === "test";

const TEST_PRODUCTS = [
  {
    id: process.env.REACT_APP_LS_PRODUCT_ID_BASIC,
    attributes: {
      name: "Basic",
      price_formatted: "$9.99/month",
      buy_now_url: "https://checkout.example.com/basic",
    },
  },
  {
    id: process.env.REACT_APP_LS_PRODUCT_ID_PRO,
    attributes: {
      name: "Pro",
      price_formatted: "$19.99/month",
      buy_now_url: "https://checkout.example.com/pro",
    },
  },
  {
    id: process.env.REACT_APP_LS_PRODUCT_ID_PRO_PLUS,
    attributes: {
      name: "Pro+",
      price_formatted: "$39.99/month",
      buy_now_url: "https://checkout.example.com/pro-plus",
    },
  },
  {
    id: process.env.REACT_APP_LS_PRODUCT_ID_LIFETIME,
    attributes: {
      name: "Lifetime",
      price_formatted: "$299.99/one time",
      buy_now_url: "https://checkout.example.com/lifetime",
    },
  },
];

const TEST_SUBSCRIPTION = [
  {
    isLifetime: false,
    subscription: {
      attributes: {
        status: "active",
        product_id: process.env.REACT_APP_LS_PRODUCT_ID_BASIC,
        ends_at: "2026-04-07T00:00:00.000000Z",
        trial_ends_at: null,
        first_order_item: {
          product_id: process.env.REACT_APP_LS_PRODUCT_ID_BASIC,
        },
        urls: {
          update_payment_method: "https://checkout.example.com/update-payment",
        },
      },
    },
  },
];

export async function GetLemonSqueezyProducts() {
  if (IS_TEST_ENV) {
    return [TEST_PRODUCTS, null];
  }
  try {
    const response = await api.get("/products");
    console.log("LemonSqueezy products:", response.data);
    return [response.data.data, null];
  } catch (error) {
    const errorObj = getErrorMsgAndCode(error);

    console.error("Error fetching LemonSqueezy products:", error);
    return [null, errorObj];
  }
}
export async function GetUserSubscription() {
  if (IS_TEST_ENV) {
    return [TEST_SUBSCRIPTION, null];
  }
  try {
    const response = await api.get(`/subscription`);
    console.log("User subscription:", response.data);
    return [response.data, null];
  } catch (error) {
    console.error("Error fetching user subscription:", error);
    const errorObj = getErrorMsgAndCode(error);
    return [null, errorObj];
  }
}
export async function CanUserPurchase(productId) {
  if (IS_TEST_ENV) {
    return [{ canPurchase: true, reason: null }, null];
  }
  try {
    const response = await api.get(`/canPurchaseProduct/${productId}`);
    console.log("Purchase eligibility:", response.data);
    return [response.data, null];
  } catch (error) {
    console.error("Error checking purchase eligibility:", error);
    const errorObj = getErrorMsgAndCode(error);
    return [null, errorObj];
  }
}
export async function InitiatePurchase(productId) {
  try {
    console.log("Purchase api called with product id:", productId);
    const response = await api.post("/purchaseProduct", {
      productId,
    });
    console.log("Purchase initiated:", response.data);
    return [response.data, null];
  } catch (error) {
    console.error("Error initiating purchase:", error);
    const errorObj = getErrorMsgAndCode(error);
    return [null, errorObj];
  }
}
export async function CancelUserSubscription() {
  try {
    await api.post("/cancelSubscription");
    console.log("Canceling sub");
    return [null, null];
  } catch (error) {
    console.error("Error canceling sub:", error);
    const errorObj = getErrorMsgAndCode(error);
    return [null, errorObj];
  }
}
export async function ResumeUserSubscription() {
  try {
    await api.post("/resumeSubscription");
    console.log("resuming sub");
    return [null, null];
  } catch (error) {
    console.error("Error resuming sub:", error);
    const errorObj = getErrorMsgAndCode(error);
    return [null, errorObj];
  }
}

// Enhanced product fetching with user subscription context
export async function GetProductsWithSubscriptionContext() {
  if (IS_TEST_ENV) {
    return [
      {
        products: TEST_PRODUCTS,
        userSubscription: TEST_SUBSCRIPTION,
      },
      null,
    ];
  }
  try {
    const [products, userSubscription] = await Promise.all([
      GetLemonSqueezyProducts(),
      GetUserSubscription(),
    ]);

    if (products[1] || userSubscription[1]) {
      return [null, products[1] || userSubscription[1]];
    }

    let enhancedUserSubscription = userSubscription;
    if (userSubscription[0]?.isLifetime) {
      enhancedUserSubscription[0].endsAt = "Never";
    }

    console.log("Advanced products return ", {
      products: products,
      userSubscription,
    });
    return [
      {
        products: products[0],
        userSubscription,
      },
      null,
    ];
  } catch (error) {
    const errorObj = getErrorMsgAndCode(error);
    console.error("Error fetching products with subscription context:", error);
    return [null, errorObj];
  }
}

// Helper function to determine if a product is the user's current plan
function isCurrentPlan(productId, userPackageId) {
  // Map your product IDs to package IDs
  const productToPackageMap = {
    [process.env.REACT_APP_LS_PRODUCT_ID_BASIC]: 1,
    [process.env.REACT_APP_LS_PRODUCT_ID_PRO]: 2,
    [process.env.REACT_APP_LS_PRODUCT_ID_PRO_PLUS]: 3,
    [process.env.REACT_APP_LS_PRODUCT_ID_LIFETIME]: 4,
  };

  return productToPackageMap[productId] === userPackageId;
}

// Handle purchase with validation
export async function HandlePurchaseClick(userId, productId, userEmail) {
  try {
    // First check if user can purchase
    const eligibility = await CanUserPurchase(userId, productId);

    if (!eligibility.canPurchase) {
      throw new Error(eligibility.reason);
    }

    // If eligible, initiate purchase
    const purchaseData = await InitiatePurchase(userId, productId, userEmail);

    // Redirect to checkout
    if (purchaseData.checkoutUrl) {
      window.location.href = purchaseData.checkoutUrl;
    }

    return [purchaseData, null];
  } catch (error) {
    const errorObj = getErrorMsgAndCode(error);
    console.error("Purchase error:", error);
    return [null, errorObj];
  }
}

// Get subscription status for UI updates
export async function GetSubscriptionStatus(userId) {
  if (IS_TEST_ENV) {
    return {
      hasActiveSubscription: true,
      isLifetime: false,
      currentPlan: "Basic",
      packageId: process.env.REACT_APP_LS_PRODUCT_ID_BASIC,
      endsAt: "2026-04-07T00:00:00.000000Z",
    };
  }
  try {
    const subscription = await GetUserSubscription(userId);

    return {
      hasActiveSubscription:
        subscription.hasSubscription && subscription.isActive,
      isLifetime: subscription.isLifetime,
      currentPlan: subscription.subscriptionPlan,
      packageId: subscription.packageId,
      endsAt: subscription.endsAt,
    };
  } catch (error) {
    const errorObj = getErrorMsgAndCode(error);
    console.error("Error getting subscription status:", error);
    return [
      {
        hasActiveSubscription: false,
        isLifetime: false,
        currentPlan: null,
        packageId: null,
        endsAt: null,
      },
      error,
    ];
  }
}
